import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  TablePagination,
  Chip,
  Autocomplete,
  TextField,
  CircularProgress,
  Breadcrumbs,
  Link,
  Stack,
} from "@mui/material";
import { Edit, Visibility } from "@mui/icons-material";
import { getUsersList } from "../../api/users";
import { getTenantsList } from "../../api/tenant";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { APP_HOME_PATH } from "../../config/routes";
import useIsMobileList from "../../hooks/useIsMobileList";
import MobileListCard, {
  MobileListCardSkeleton,
} from "../../components/MobileListCard";

function UserCard({ user, isSuperAdmin, onView, onEdit }) {
  return (
    <MobileListCard
      title={`${user.firstname} ${user.lastname}`}
      subtitle={user.email}
      status={
        <Chip
          label={user.is_active ? "Active" : "Inactive"}
          color={user.is_active ? "success" : "default"}
          size="small"
        />
      }
      fields={[
        ...(isSuperAdmin
          ? [{ label: "Organization", value: user.tenant_name || "—" }]
          : []),
        {
          label: "Roles",
          value: (
            <Stack direction="row" flexWrap="wrap" gap={0.5} justifyContent="flex-end">
              {(user.roles || []).map((role) => (
                <Chip
                  key={role.id || role}
                  label={typeof role === "string" ? role : role.name}
                  size="small"
                />
              ))}
            </Stack>
          ),
        },
      ]}
      actions={
        <>
          <IconButton aria-label="Edit user" onClick={() => onEdit(user.id)}>
            <Edit />
          </IconButton>
          <IconButton aria-label="View user" onClick={() => onView(user.id)}>
            <Visibility />
          </IconButton>
        </>
      }
    />
  );
}

const UserList = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState(null);
  const navigate = useNavigate();
  const isMobile = useIsMobileList("md");

  const isSuperAdmin = currentUser?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await getTenantsList({ for_filter: true });
        setTenants(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error("Failed to fetch tenants:", error);
        setTenants([]);
      }
    };
    if (isSuperAdmin) fetchTenants();
  }, [isSuperAdmin]);

  useEffect(() => {
    setPage(0);
  }, [tenantFilter]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const params = {
          skip: page * rowsPerPage,
          limit: rowsPerPage,
        };
        if (isSuperAdmin) {
          if (tenantFilter?.id != null) {
            params.tenant_id = Number(tenantFilter.id);
          }
        } else if (currentUser?.tenant_id != null) {
          params.tenant_id = currentUser.tenant_id;
        }

        const { items, total } = await getUsersList(params);
        setUsers(items);
        setTotalCount(total);
      } catch (error) {
        console.error("Failed to fetch users:", error);
        setUsers([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page, rowsPerPage, tenantFilter, isSuperAdmin, currentUser?.tenant_id]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </Link>
        <Typography color="text.primary">Users</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>
        User management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add people with{" "}
        <Link component={RouterLink} to="/admin/invites">
          invitations
        </Link>
        . Here you can review accounts and update details.
      </Typography>

      {isSuperAdmin && (
        <Box
          sx={{
            width: "100%",
            maxWidth: 720,
            minWidth: { xs: "100%", sm: 360 },
            mb: 3,
          }}
        >
          <Autocomplete
            sx={{ width: "100%" }}
            options={tenants}
            getOptionLabel={(option) => option?.name ?? ""}
            value={tenantFilter}
            onChange={(_, newValue) => setTenantFilter(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by organization"
                placeholder="All organizations"
                fullWidth
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              />
            )}
            isOptionEqualToValue={(option, value) =>
              option?.id != null &&
              value?.id != null &&
              Number(option.id) === Number(value.id)
            }
            clearOnEscape
            disableListWrap
            slotProps={{
              listbox: {
                sx: { minWidth: 280 },
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Leave empty to list users across all organizations, or pick one farm to narrow the table.
          </Typography>
        </Box>
      )}

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <MobileListCardSkeleton key={i} />
              ))
            : users.length === 0
              ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                  No users match this view.
                </Typography>
              )
              : users.map((user) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    isSuperAdmin={isSuperAdmin}
                    onView={(id) => navigate(`/admin/users/${id}`)}
                    onEdit={(id) => navigate(`/admin/users/${id}/edit`)}
                  />
                ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ position: "relative" }}>
          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.6)",
                zIndex: 1,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                {isSuperAdmin && <TableCell>Organization</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={isSuperAdmin ? 6 : 5}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography color="text.secondary">
                      No users match this view.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      {user.firstname} {user.lastname}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {(user.roles || []).map((role) => (
                        <Chip
                          key={role.id || role}
                          label={typeof role === "string" ? role : role.name}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell>{user.tenant_name || "—"}</TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={user.is_active ? "Active" : "Inactive"}
                        color={user.is_active ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label="Edit user"
                        onClick={() => navigate(`/admin/users/${user.id}/edit`)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        aria-label="View user"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default UserList;
